import { SearchableTagRepository } from 'lc-repository'
import {
  fromInsideBoundingBoxToCoordinatesBoundaries,
  DEFAULT_COORDINATES_BOUNDARIES,
  fromCoordinatesToBoundaries,
} from '~/utils/algolia/services'
import type { SupportedCurrencies } from 'lc-services/types'
import type { QueryModelType, StateSearchResults } from '@@/types/search/types'

export const useSearchResults = ({
  startDate,
  endDate,
}: {
  startDate: ComputedRef<string | null>
  endDate: ComputedRef<string | null>
}) => {
  const stateSearchResults = useState<StateSearchResults>(
    'state-search-results',
  )

  const { $lcServicesSearch, $lcRepositories, $i18n } = useNuxtApp()
  const { searchController, searchPresenter } = $lcServicesSearch
  const { algoliaIds } = useAuth()

  const locale = computed(() => $i18n.locale.value)
  const isLoading = computed(() => stateSearchResults.value.isLoading)
  const hits = computed(() => stateSearchResults.value.hits)
  const hitsMap = computed(() => stateSearchResults.value.hitsMap)
  const hitsCoordinatesMap = computed(
    () => stateSearchResults.value.hitsCoordinatesMap,
  )
  const housesPeriodIds = computed(
    () => stateSearchResults.value.housesPeriodIds,
  )
  const coordinatesBoundaries = computed<[number, number][]>(() =>
    stateSearchResults.value.coordinatesBoundaries?.min_coords &&
    stateSearchResults.value.coordinatesBoundaries.max_coords
      ? [
          stateSearchResults.value.coordinatesBoundaries.min_coords,
          stateSearchResults.value.coordinatesBoundaries.max_coords,
        ]
      : DEFAULT_COORDINATES_BOUNDARIES,
  )
  const nbHits = computed(() => stateSearchResults.value.nbHits)
  const nbPages = computed(() => stateSearchResults.value.nbPages)
  const page = computed(() => stateSearchResults.value.page)
  const facets = computed(() => stateSearchResults.value.facets)
  const hasResults = computed(() =>
    Boolean(hits.value.length || hitsMap.value.length),
  )
  const hasResultsSplit = computed(() =>
    Boolean(hitsCoordinatesMap.value.length),
  )
  const keepOldResults = computed(
    () => stateSearchResults.value.meta.keepOldResults,
  )
  const initClientSide = computed(
    () => stateSearchResults.value.meta.initClientSide,
  )
  const housesPerPage = computed(
    () => stateSearchResults.value.meta.housesPerPage,
  )
  const showPreviousButton = computed(
    () => stateSearchResults.value.meta.showPreviousButton,
  )
  const algoliaQueryId = computed(
    () => stateSearchResults.value.meta.algoliaQueryId,
  )
  const searchableTagsIdentifiers = computed(
    () => stateSearchResults.value.searchableTagsIdentifiers,
  )

  const invalidHouses = computed(() => {
    let firstHouseId = null
    let secondHouseId = null

    // Get first house
    if (hits.value.some((h) => h.allMinimumDurationValid === false)) {
      firstHouseId = hits.value.find(
        (h) => h.allMinimumDurationValid === false,
      )?.id
    }
    // Get second house
    if (
      hits.value.filter((h) => h.allMinimumDurationValid === false).length >= 2
    ) {
      secondHouseId = hits.value.filter(
        (h) => h.allMinimumDurationValid === false,
      )[1].id
    }

    return {
      first: firstHouseId,
      second: secondHouseId,
    }
  })

  const hasOnlyInvalidResults = computed(() => {
    // Date + has one invalid house + no valid house
    return (
      startDate.value &&
      endDate.value &&
      hits.value.some((h) => h.allMinimumDurationValid === false) &&
      !hits.value.some((h) => h.allMinimumDurationValid === true)
    )
  })

  const fetchSplitResults = async ({
    currency,
    searchHousesPayload,
    requestInfo,
  }: {
    currency: Ref<SupportedCurrencies>
    searchHousesPayload: QueryModelType
    requestInfo: { lastId: number }
  }) => {
    await searchController.searchHousesSplitMap(
      searchHousesPayload,
      locale.value,
      currency.value,
      algoliaIds.value,
      'web',
      requestInfo,
    )

    if (showPreviousButton.value && page.value === 0) {
      stateSearchResults.value.meta.showPreviousButton = false
    }

    let results = []
    if (keepOldResults.value) {
      const hitsId = stateSearchResults.value.hits.map((h) => h.id)
      const newHits = searchPresenter.vm.searchHousesSpitListResult.filter(
        (h) => !hitsId.includes(h.id),
      )
      results.push(...hits.value, ...newHits)
    } else {
      results = searchPresenter.vm.searchHousesSpitListResult
    }

    if (import.meta.client) {
      stateSearchResults.value.meta.initClientSide = true
    }

    const haveHousesPeriodsIds = Boolean(
      stateSearchResults.value.housesPeriodIds.validHousePeriodsIds.length ||
        stateSearchResults.value.housesPeriodIds.invalidHousePeriodsIds.length,
    )

    stateSearchResults.value = {
      ...stateSearchResults.value,
      facets: searchPresenter.vm.facets,
      isLoading: false,
      hits: results,
      hitsMap: [],
      housesPeriodIds: haveHousesPeriodsIds
        ? stateSearchResults.value.housesPeriodIds
        : searchPresenter.vm.housesPeriodIds || {
            invalidHousePeriodsIds: [],
            validHousePeriodsIds: [],
          },
      hitsCoordinatesMap: keepOldResults.value
        ? stateSearchResults.value.hitsCoordinatesMap
        : searchPresenter.vm.searchHousesSpitMapResult,
      meta: {
        ...stateSearchResults.value.meta,
        algoliaQueryId: searchPresenter.vm.algoliaQueryId,
      },
      nbHits: keepOldResults.value
        ? stateSearchResults.value.nbHits
        : searchPresenter.vm.nbHouses,
      nbPages: keepOldResults.value
        ? stateSearchResults.value.nbPages
        : searchPresenter.vm.nbPages,
      page: searchPresenter.vm.searchCurrentPage,
      coordinatesBoundaries:
        fromInsideBoundingBoxToCoordinatesBoundaries(
          searchHousesPayload.insideBoundingBox,
        ) ||
        fromCoordinatesToBoundaries(
          searchHousesPayload.aroundLatLng,
          searchHousesPayload.aroundRadius,
        ) ||
        searchPresenter.vm.coordinatesBoundaries,
    }

    return results
  }

  const fetchHouseResults = async ({
    currency,
    searchHousesPayload,
  }: {
    currency: Ref<SupportedCurrencies>
    searchHousesPayload: QueryModelType
  }) => {
    stateSearchResults.value.isLoading = true

    await searchController.searchHousesV2(
      searchHousesPayload,
      locale.value,
      currency.value,
      algoliaIds.value,
      'web',
    )

    if (showPreviousButton.value && page.value === 0) {
      stateSearchResults.value.meta.showPreviousButton = false
    }

    let results = []
    if (keepOldResults.value) {
      const hitsId = stateSearchResults.value.hits.map((h) => h.id)
      const newHits = searchPresenter.vm.searchHousesResult.filter(
        (h) => !hitsId.includes(h.id),
      )
      results.push(...hits.value, ...newHits)
    } else {
      results = searchPresenter.vm.searchHousesResult
    }

    if (import.meta.client) {
      stateSearchResults.value.meta.initClientSide = true
    }

    stateSearchResults.value = {
      ...stateSearchResults.value,
      facets: searchPresenter.vm.facets,
      isLoading: false,
      hits: results,
      hitsMap: [],
      meta: {
        ...stateSearchResults.value.meta,
        algoliaQueryId: searchPresenter.vm.algoliaQueryId,
      },
      nbHits: searchPresenter.vm.nbHouses,
      nbPages: searchPresenter.vm.nbPages,
      page: searchPresenter.vm.searchCurrentPage,
      coordinatesBoundaries: searchPresenter.vm.coordinatesBoundaries,
    }
  }

  const fetchHouseMapResults = async ({
    currency,
    searchHousesPayload,
  }: {
    currency: Ref<SupportedCurrencies>
    searchHousesPayload: QueryModelType
  }) => {
    stateSearchResults.value.isLoading = true

    await searchController.searchHousesMap(
      {
        ...searchHousesPayload,
        locale: locale.value,
        currency: currency.value,
      },
      algoliaIds.value,
      'web',
    )

    stateSearchResults.value = {
      ...stateSearchResults.value,
      facets: searchPresenter.vm.facets,
      isLoading: false,
      hits: [],
      hitsMap: searchPresenter.vm.houseCoordinates?.features || [],
      nbHits: searchPresenter.vm.nbHouses,
      nbPages: searchPresenter.vm.nbPages,
      page: searchPresenter.vm.searchCurrentPage,
      coordinatesBoundaries:
        fromInsideBoundingBoxToCoordinatesBoundaries(
          searchHousesPayload.insideBoundingBox,
        ) || searchPresenter.vm.coordinatesBoundaries,
    }
  }

  const fetchSearchableTagsIdentifiers = async () => {
    if (searchableTagsIdentifiers.value.length) return

    const { data, errors } = await SearchableTagRepository(
      $lcRepositories.httpClient,
    ).getIdentifiers()

    stateSearchResults.value.searchableTagsIdentifiers =
      errors || !data ? [] : data
  }

  const setPage = (page: number) => {
    stateSearchResults.value.page = page
  }

  const setKeepOldResults = (keepOldResults: boolean) => {
    stateSearchResults.value.meta.keepOldResults = keepOldResults
  }

  const setIsLoading = (value: boolean) => {
    stateSearchResults.value.isLoading = value
  }

  const resetHousesPeriodIds = () => {
    stateSearchResults.value.housesPeriodIds = {
      invalidHousePeriodsIds: [],
      validHousePeriodsIds: [],
    }
  }

  return {
    algoliaQueryId,
    coordinatesBoundaries,
    facets,
    fetchSearchableTagsIdentifiers,
    fetchHouseMapResults,
    fetchHouseResults,
    fetchSplitResults,
    hasOnlyInvalidResults,
    hasResultsSplit,
    hasResults,
    hits,
    hitsCoordinatesMap,
    hitsMap,
    housesPeriodIds,
    housesPerPage,
    initClientSide,
    invalidHouses,
    isLoading,
    keepOldResults,
    nbHits,
    nbPages,
    page,
    resetHousesPeriodIds,
    searchableTagsIdentifiers,
    setIsLoading,
    setKeepOldResults,
    setPage,
    showPreviousButton,
  }
}
